import {
  Button,
  Col,
  Input,
  Pagination,
  Popover,
  Row,
  Select,
  Skeleton,
  DatePicker,
  Table,
} from "antd";
import { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { FaEye, FaSearch, FaTrash } from "react-icons/fa";
import { MdTune } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { Get } from "../../config/api/get";
import { Delete } from "../../config/api/delete";
import { ADMIN, UPLOADS_URL } from "../../config/constants/api";
import { useDebouncedCallback } from "use-debounce";
import dayjs from "dayjs";
import Swal from "sweetalert2";

const UserManagement = () => {
  const token = useSelector((state) => state.user.userToken);
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [paginationConfig, setPaginationConfig] = useState({
    pageNumber: 1,
    limit: 10,
    totalDocs: 0,
    totalPages: 0,
  });

  const [filter, setFilter] = useState({
    status: null,
    type: null,
    keyword: "",
    from: null,
    to: null,
    membership: null
  });
  
  // Ref to store latest filter for debounced callback
  const filterRef = useRef(filter);
  
  // Update ref whenever filter changes
  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);
  
  // Unified function to fetch users with all filters
  const getUsers = (pageNumber, pageSize, filters = null) => {
    setLoading(true);
    const activeFilters = filters || filter;
    
    // Format dates if they exist
    let fromDate = null;
    let toDate = null;
    if (activeFilters.from) {
      fromDate = dayjs(activeFilters.from).format("YYYY-MM-DD");
    }
    if (activeFilters.to) {
      toDate = dayjs(activeFilters.to).format("YYYY-MM-DD");
    }
    
    // Build query parameters
    const queryParams = {
      page: pageNumber
        ? pageNumber.toString()
        : paginationConfig.pageNumber.toString(),
      limit: pageSize ? pageSize.toString() : paginationConfig.limit.toString(),
    };
    
    // Add optional filters only if they have values
    if (activeFilters.keyword) {
      queryParams.keyword = activeFilters.keyword;
    }
    if (activeFilters.status) {
      queryParams.status = activeFilters.status;
    }
    if (activeFilters.membership) {
      queryParams.membership = activeFilters.membership;
    }
    if (fromDate) {
      queryParams.from = fromDate;
    }
    if (toDate) {
      queryParams.to = toDate;
    }
    
    Get(ADMIN.getUsers, token, queryParams)
      .then((response) => {
        if (response?.data?.docs) {
          setUsers(response?.data?.docs);
          setPaginationConfig({
            pageNumber: response?.data?.page,
            limit: response?.data?.limit,
            totalDocs: response?.data?.totalDocs,
            totalPages: response?.data?.totalPages,
          });
          setLoading(false);
        }
      })
      .catch((err) => {
        console.log("Error Fetching Loads", err);
        setLoading(false);
      });
  };
  useEffect(() => {
    getUsers();
    //eslint-disable-next-line
  }, []);
  // const capitalizeFirstLetter = (str) => {
  //   return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  // };

  const handlePageChange = (pageNumber) => {
    setPaginationConfig({
      ...paginationConfig,
      pageNumber: pageNumber,
    });
    // Preserve all active filters when changing page
    getUsers(pageNumber, paginationConfig.limit, filter);
  };

  // Debounced function for API call - uses ref to get latest filter state
  const debouncedSearch = useDebouncedCallback((searchValue) => {
    // Get the latest filter state from ref (updated by useEffect)
    const currentFilter = filterRef.current;
    const updatedFilter = {
      ...currentFilter,
      keyword: searchValue,
    };
    // Apply search with all existing filters
    getUsers(paginationConfig.pageNumber, paginationConfig.limit, updatedFilter);
  }, 1000);

  const handleSearch = (value) => {
    // Update filter state immediately for responsive UI
    const updatedFilter = {
      ...filter,
      keyword: value,
    };
    setFilter(updatedFilter);
    // Trigger debounced API call with the search value
    // The ref will be updated by useEffect, so the debounced function will use latest filter
    debouncedSearch(value);
  };

  const handleStatusChange = (value) => {
    setFilter({
      ...filter,
      status: value,
    });
  };
  const handleMembershipChange = (value) => {
    setFilter({
      ...filter,
      membership: value,
    });
  };

  // const handleTypeChange = (value) => {
  //   setFilter({
  //     ...filter,
  //     type: value,
  //   });
  // };

  const handleOpenChange = (newOpen) => {
    setOpen(newOpen);
  };

  const handleFrom = (date) => {
    setFilter({
      ...filter,
      from: date,
    });
  };

  const handleTo = (date) => {
    setFilter({
      ...filter,
      to: date,
    });
  };
  
  const handleApply = () => {
    // Apply all filters together (status, membership, dates) with existing keyword
    getUsers(paginationConfig.pageNumber, paginationConfig.limit, filter);
    setOpen(false); // Close the filter popover after applying
  };
  
  const handleClear = () => {
    const clearedFilter = {
      status: null,
      type: null,
      keyword: "", // Clear search keyword too
      from: null,
      to: null,
      membership: null,
    };
    setFilter(clearedFilter);
    getUsers(1, paginationConfig.limit, clearedFilter);
    setOpen(false); // Close the filter popover after clearing
  };

  const handleDelete = (userId, userName) => {
    Swal.fire({
      title: "Are you sure?",
      html: `
        <p>You are about to delete user: <strong>${userName || 'this user'}</strong></p>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      focusConfirm: false,
      focusCancel: true,
    }).then((result) => {
      if (result.isConfirmed) {
        // Show loading state
        Swal.fire({
          title: "Deleting...",
          text: "Please wait while we delete the user.",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        Delete(ADMIN.deleteUser + userId, token, null)
          .then((response) => {
            // Refresh the users list immediately after successful deletion
            // Use filterRef to get the latest filter state to avoid stale closure
            const currentFilter = filterRef.current;
            const currentPage = paginationConfig.pageNumber;
            const currentPageSize = paginationConfig.limit;
            
            if (response?.success || response?.status) {
              // Refresh the table immediately before showing success message
              getUsers(currentPage, currentPageSize, currentFilter);
              
              Swal.fire({
                title: "Deleted!",
                text: response?.message || "User deleted successfully.",
                icon: "success",
                confirmButtonColor: "#3085d6",
              });
            } else {
              Swal.fire({
                title: "Error!",
                text: response?.message || "Failed to delete user.",
                icon: "error",
                confirmButtonColor: "#d33",
              });
            }
          })
          .catch((err) => {
            const errorMessage = err?.response?.data?.message || err?.message || "Failed to delete user.";
            
            // Still try to refresh in case the deletion actually succeeded
            const currentFilter = filterRef.current;
            const currentPage = paginationConfig.pageNumber;
            const currentPageSize = paginationConfig.limit;
            getUsers(currentPage, currentPageSize, currentFilter);
            
            Swal.fire({
              title: "Error!",
              text: errorMessage,
              icon: "error",
              confirmButtonColor: "#d33",
            });
          });
      }
    });
  };

  const startIndex =
    (paginationConfig.pageNumber - 1) * paginationConfig.limit + 1;
  const endIndex = Math.min(
    startIndex + paginationConfig.limit - 1,
    paginationConfig.totalDocs
  );
  const message = users
  ? `Showing records ${endIndex} of ${paginationConfig.totalDocs}`
  : "Showing records 0 of 0";



  const itemRender = (_, type, originalElement) => {
    if (type === "prev") {
      return (
        <button type="button" className="pagination-link">
          Previous
        </button>
      );
    }
    if (type === "next") {
      return (
        <button type="button" className="pagination-link">
          Next
        </button>
      );
    }

    return originalElement;
  };
  const filterContent = (
    <div className="filterDropdown">
      <div>
        <p
          className="mainLabel"
          style={{ padding: "10px", fontSize: "18px", fontWeight: "bold" }}
        >
          Filter
        </p>
      </div>
      <hr style={{ marginBottom: 10 }} />

      <div className="filterDropdownBody">
        <p className="mainLabel">From Date:</p>
        <DatePicker
          size={"large"}
          className="filterSelectBox"
          value={filter.from ? dayjs(filter.from) : null}
          onChange={(date) => handleFrom(date)}
          style={{
            width: "100%",
            marginBottom: "0px",
            textAlign: "left",
          }}
          format="YYYY-MM-DD"
        />
        
        <p className="mainLabel">To Date:</p>
        <DatePicker
          size={"large"}
          className="filterSelectBox"
          value={filter.to ? dayjs(filter.to) : null}
          onChange={(date) => handleTo(date)}
          style={{
            width: "100%",
            marginBottom: "0px",
            textAlign: "left",
          }}
          format="YYYY-MM-DD"
        />

        <p className="mainLabel">Status:</p>
        <Select
          size={"large"}
          className="filterSelectBox"
          placeholder="Select Status"
          value={filter.status}
          onChange={(e) => handleStatusChange(e)}
          allowClear
          style={{
            width: "100%",
            marginBottom: "10px",
            textAlign: "left",
          }}
          options={[
            { value: "ACTIVE", label: "Active" },
            { value: "INACTIVE", label: "In-Active" },
          ]}
        />
        
        <p className="mainLabel">Membership:</p>
        <Select
          size={"large"}
          className="filterSelectBox"
          placeholder="Select Membership"
          value={filter.membership}
          onChange={(e) => handleMembershipChange(e)}
          allowClear
          style={{
            width: "100%",
            marginBottom: "10px",
            textAlign: "left",
          }}
          options={[
            { value: "Weekly Membership", label: "Weekly" },
            { value: "Monthly Membership", label: "Monthly" },
            { value: "Yearly Membership", label: "Yearly" },
          ]}
        />

        <Button
          type=""
          block
          size={"large"}
          style={{ marginBottom: "10px" }}
          className="web-btn"
          //   onClick={() => getUsers()}
          onClick={handleApply}
        >
          Apply
        </Button>
        <Button
          type=""
          block
          size={"large"}
          className="web-btn"
          onClick={handleClear}
        >
          Clear All
        </Button>
      </div>
    </div>
  );

  const columns = [
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          S.NO
        </p>
      ),
      dataIndex: "key",
      key: "key",
      render: (value, item, index) => (
        <p className="text-xs text-center font-euclid text-[#575757]">
          {(index < 10 && "0") + (index + 1)}
        </p>
      ),
    },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          image
        </p>
      ),
      dataIndex: "image",
      key: "image",
      render: (value, item, index) => {
        const handleImageError = (e) => {
          e.target.src = "/assets/images/avatar.png";
        };
        
        const imageSrc = item?.image 
          ? UPLOADS_URL + item.image 
          : "/assets/images/avatar.png";
        
        return (
          <div className="flex justify-center">
            <img 
              src={imageSrc} 
              alt="profile" 
              onError={handleImageError}
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "100%",
                objectFit: "cover",
                objectPosition: "center",
              }}
            />
          </div>
        );
      },
    },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          Name
        </p>
      ),
      dataIndex: "name",
      key: "name",
      render: (value, item, index) => (
        <div className="flex flex-col items-center justify-center">
          <p className="text-xs text-center font-euclid text-[#575757]">
            {item?.firstName + " " + item?.lastName}
          </p>
        </div>
      ),
    },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          Email
        </p>
      ),
      dataIndex: "email",
      key: "email",
      render: (value, item, index) => (
        <p className="text-xs text-center font-euclid text-[#575757]">
          {item.email}
        </p>
      ),
    },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          Phone No
        </p>
      ),
      dataIndex: "phone",
      key: "phone",
      render: (value, item, index) => (
        <p className="text-xs text-center font-euclid text-[#575757]">
          {item.phone}
        </p>
      ),
    },

    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          Total Bookings
        </p>
      ),
      dataIndex: "totalBookings",
      key: "totalBookings",
      render: (value, item, index) => (
        <p className="text-xs text-center font-euclid text-[#575757]">
          {item?.totalBookings}
        </p>
      ),
    },
    // {
    //   title: (
    //     <p className="uppercase font-euclid text-black font-bold text-xs text-center">
    //       cancel bookings
    //     </p>
    //   ),
    //   dataIndex: "cancelBookings",
    //   key: "cancelBookings",
    //   render: (value, item, index) => (
    //     <p className="text-xs text-center font-euclid text-[#575757]">
    //       {item.cancelBookings}
    //     </p>
    //   ),
    // },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          avg rating (Total rating)
        </p>
      ),
      dataIndex: "averageRating",
      key: "averageRating",
      render: (value, item, index) => (
        <p className="text-xs text-center font-euclid text-[#575757]">
          {item?.averageRating ? item?.averageRating : "0"}
        </p>
      ),
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (item) => (
        <>
          {item === "INACTIVE" ? (
            <span style={{ color: "#ff0000" }}>{item}</span>
          ) : item === "ACTIVE" ? (
            <span style={{ color: "#00D640" }}>{item}</span>
          ) : (
            <span>{item}</span>
          )}
        </>
      ),
    },

    // {
    //   title: (
    //     <p className="uppercase font-euclid text-black font-bold text-xs text-center">
    //       status
    //     </p>
    //   ),
    //   dataIndex: "status",
    //   key: "status",
    //   render: (value, item, index) => (
    //     <div className="flex justify-center customSwitch2">
    //       <Switch defaultChecked />
    //     </div>
    //   ),
    // },
    {
      title: (
        <p className="uppercase font-euclid text-black font-bold text-xs text-center">
          action
        </p>
      ),
      dataIndex: "_id",
      key: "_id",
      render: (value, item, index) => (
        <div className="flex justify-center items-center gap-2">
          <div className="flex justify-center bg-[#9CFEC4] rounded-[100%] w-fit p-2">
            <FaEye
              className="text-[#089B44] cursor-pointer h-[16px] w-[16px]"
              onClick={() => navigate("/UserDetails/" + item._id ,  { state: { status: item.status }})}
            />
          </div>
          <div className="flex justify-center bg-[#FFE6E6] rounded-[100%] w-fit p-2">
            <FaTrash
              className="text-[#DC3545] cursor-pointer h-[16px] w-[16px]"
              onClick={() => handleDelete(item._id, `${item?.firstName} ${item?.lastName}`)}
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <Row justify="center">
      <Col md={23} xs={22}>
        <div className="flex justify-between items-center mt-8">
          <h3 className="font-tavi lg:text-3xl text-2xl max-sm:text-lg font-normal text-black ml-8">
            User Management
          </h3>
          <div className="flex justify-center items-center">
            <Input
              size="large"
              className="mainInput dashInput w-[700px] max-md:w-full max-md:max-w-full"
              placeholder="Search by name, email, or phone number"
              value={filter.keyword}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ fontSize: "14px" }}
              suffix={
                <FaSearch
                  style={{
                    color: "#818181",
                    fontSize: 16,
                    cursor: "pointer",
                  }}
                />
              }
            />
            &emsp;
            <Popover
              content={filterContent}
              trigger="click"
              open={open}
              onOpenChange={handleOpenChange}
              placement="bottomRight"
              arrow={false}
            >
              <Button
                style={{
                  padding: "4px 11px",
                  height: "auto",
                  borderRadius: "6px",
                  backgroundColor: "white",
                }}
              >
                <MdTune style={{ fontSize: "32px", color: "#767676" }} />
              </Button>
            </Popover>
            &emsp;
            <button
              className="uppercase bg-button py-2 px-4 xl:px-16 text-black rounded-lg font-bold text-sm max-md:text-[10px] max-md:py-3 max-md:px-4"
              onClick={() => navigate("/addUser")}
            >
              Add User
            </button>
          </div>
        </div>
        <div
          className="bg-white rounded-lg  py-8 my-8"
          style={{ boxShadow: "1px 1px 1px 1px rgba(0, 0, 0, 0.05)" }}
        >
          <Row style={{ overflow: "auto" }} className="px-6">
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Skeleton active />
                <br />
              </div>
            ) : (
              <Table
                className="styledTable"
                dataSource={users}
                columns={columns}
                pagination={false}
              />
            )}
          </Row>
          <Row style={{ padding: "10px 20px" }}>
            <Col xs={24} md={12}>
              <p className="font-euclid text-sm text-black">{message}</p>
            </Col>
            <Col
              xs={24}
              md={12}
              style={{ display: "flex", justifyContent: "flex-end" }}
            >
              <Pagination
                className="styledPagination"
                onChange={(e) => handlePageChange(e)}
                current={parseInt(paginationConfig.pageNumber)}
                pageSize={paginationConfig.limit}
                total={paginationConfig.totalDocs}
                itemRender={itemRender}
              />
            </Col>
          </Row>
        </div>
      </Col>
    </Row>
  );
};

export default UserManagement;
